import React from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import {
  Banner,
  Box,
  Button,
  Dialog,
  H1,
  H2,
  H4,
  HStack,
  Icon,
  Separator,
  Tag,
  Text,
  VStack,
} from '@betterment/samba-react';
import { Wrap } from '@betterment/samba-styled-system/jsx';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { addAccountUrl } from '../../../Shared/addAccountUrl';
import { urlBuilder } from '../../../Shared/urlBuilder';
import {
  CreateAccountRecommendationDocument,
  CreateAccountRecommendationMutation,
} from '../graphql/mutation.createAccountRecommendation.generated';
import { AccountRecommendationOptionDocument } from '../graphql/query.accountRecommendationOption.generated';

const GRADIENT_SHIFT_KEYFRAMES = `
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const gradientCss = {
  background:
    'linear-gradient(90deg, token(colors.primitives.gold.40), token(colors.primitives.blue.20), token(colors.primitives.gold.40))',
  backgroundSize: '200% 200%',
  animation: 'gradientShift 3s ease infinite',
  padding: '1px',
};

const InitialContent: React.FC<{ variablesDescriptions?: string[] | null }> = ({ variablesDescriptions }) => {
  const descriptionsWithKeys = useFallbackKeys(variablesDescriptions || []);
  return (
    <>
      <VStack css={{ gap: 'space.150' }}>
        <Dialog.Title asChild={true}>
          <H2 variant="title300">Next best accounts for you</H2>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Text variant="text100" color="text.weak">
            We'll recommend the next best accounts for you based on your financial situation with help from our
            expert-trained AI.
          </Text>
        </Dialog.Description>
      </VStack>
      <Banner>
        <Banner.Content>
          <HStack css={{ gap: 'space.100', alignItems: 'flex-start' }}>
            <Text variant="text100">
              BettermentAI is trained by our team of CFP® advisors. Your personal data isn't shared.
            </Text>
          </HStack>
        </Banner.Content>
      </Banner>
      <Separator />
      <VStack css={{ gap: 'space.50' }}>
        <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <H4 variant="title200" color="text.weak">
            Is this info still correct?
          </H4>
          <SambaAutoLink to={urlBuilder.appFinancialProfileEdit.absolute()}>Edit</SambaAutoLink>
        </HStack>
        <Text variant="text50" color="text.weak">
          We use this to provide your recommendation.
        </Text>
      </VStack>
      {variablesDescriptions && (
        <Wrap>
          {descriptionsWithKeys.map(({ key, item }) => (
            <Tag key={key} accent="grey">
              {item}
            </Tag>
          ))}
        </Wrap>
      )}
    </>
  );
};

type RecommendationContentProps = {
  recommendation: NonNullable<CreateAccountRecommendationMutation['createAccountRecommendation']['result']>;
};

const RecommendationContent: React.FC<RecommendationContentProps> = ({ recommendation }) => {
  const linkDetails = (() => {
    const option = recommendation.productOnboardingOption;
    if (option.__typename === 'GoalCreationOption') {
      const url = addAccountUrl({ product_category: option.legacyProductCategory });
      return { url, text: 'Open this account' };
    }

    if (option.__typename === 'CheckingAccountCreationOption') {
      return { url: urlBuilder.getAppCheckingAbout.absolute(), text: 'Open this account' };
    }

    if (option.__typename === 'Solo401kOnboardingOption') {
      const pc = option.legacyProductCategory;
      const url = urlBuilder.appAdviceInitiationFlow.absolute({ signup_intent: pc, product_category: pc });
      return { url, text: 'Get started' };
    }

    return null;
  })();

  const { productOnboardingOption, reasoning } = recommendation;
  return (
    <VStack css={{ gap: 'space.400' }}>
      <VStack css={{ gap: 'space.100' }}>
        <HStack css={{ gap: 'space.200', alignItems: 'center' }}>
          <ThemedImage themedAsset={productOnboardingOption.image} alt="" />
          <H1 variant="title300" color="text">
            {productOnboardingOption.name}
          </H1>
        </HStack>
        <Text variant="text100" color="text">
          {productOnboardingOption.shortDescription}
        </Text>
      </VStack>
      <VStack css={{ borderRadius: '8px', ...gradientCss }}>
        <HStack css={{ gap: 'space.100', alignItems: 'center', px: 'space.100', py: 'space.100' }}>
          <Icon name="Guidance" size="xsmall" color="icon.accent.gold" />
          <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
            Why this makes sense for you
          </Text>
        </HStack>
        <Box css={{ px: 'space.200', py: 'space.200', borderRadius: '8px', background: 'background.surface' }}>
          <Text variant="text100" color="text" dangerouslySetInnerHTML={{ __html: reasoning }} />
        </Box>
      </VStack>
      {linkDetails && (
        <SambaAutoLink to={linkDetails.url}>
          <Button variant="solidPrimary">{linkDetails.text}</Button>
        </SambaAutoLink>
      )}
    </VStack>
  );
};

const Content: React.FC = () => {
  const { data: optionData } = useGraphQLQuery(AccountRecommendationOptionDocument, { fetchPolicy: 'cache-first' });
  const [createAccountRecommendation, { data, loading }] = useGraphQLMutation(CreateAccountRecommendationDocument);

  const recommendation = data?.createAccountRecommendation?.result;
  const { variablesDescriptions } = optionData?.accountRecommendationOption || {};

  return (
    <VStack css={{ gap: 'space.200' }}>
      <style>{GRADIENT_SHIFT_KEYFRAMES}</style>
      {recommendation ? (
        <RecommendationContent recommendation={recommendation} />
      ) : (
        <>
          <InitialContent variablesDescriptions={variablesDescriptions} />
          <HStack css={{ gap: 'space.100', marginTop: 'space.200' }}>
            <Button onClick={() => createAccountRecommendation()} disabled={loading} variant="solidPrimary">
              {loading ? 'Loading...' : 'See my recommendation'}
            </Button>
            <Dialog.Close asChild={true}>
              <Button variant="ghostPrimary">I'm not interested</Button>
            </Dialog.Close>
          </HStack>
        </>
      )}
    </VStack>
  );
};

export const AccountRecommendationModal: React.FC = () => {
  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Button variant="textSecondary" css={{ borderRadius: '20px', textDecoration: 'none', ...gradientCss }}>
          <HStack
            css={{
              px: 'space.100',
              py: 'space.50',
              borderRadius: '20px',
              background: 'background.surface',
              gap: 'space.50',
              alignItems: 'center',
            }}
          >
            <Icon name="Guidance" size="xsmall" color="icon.accent.gold" />
            <Text variant="text50" color="text" css={{ lineHeight: '1' }}>
              Get a recommendation
            </Text>
          </HStack>
        </Button>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ p: 'space.400', width: '60%', maxWidth: '800px' }}>
        <ErrorBoundary fallbackType="full" feature="account recommendation" owner="agents">
          <IdempotencyProviderWithFeatureRegistry featureName="createAccountRecommendation">
            <Content />
          </IdempotencyProviderWithFeatureRegistry>
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
